import { newValorSession, type Valor, type Valores as ValoresContent, type ValorVersion } from '@ads/shared';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import * as Ic from '../components/icons';
import { Actions, useViewMeta } from '../components/Shell';
import { dateTime } from '../lib/format';
import { useConsolidate, useValoresSuggest } from '../lib/queries';
import { useToolDraft } from '../lib/useToolDraft';

/* ─────────────────────────────────────────────────────────────────────
   Valores. Dois quadros sobre a mesma lista: à esquerda, os nomes; à
   direita, o detalhamento de cada um. A caneta abre a edição e mostra as
   cinco últimas versões daquele valor, para restaurar. A IA só entra no
   segundo quadro: "Sugestão via IA" escreve os detalhamentos — preenche
   os vazios e sugere, ao lado dos que já têm texto.
   ───────────────────────────────────────────────────────────────────── */

type Field = 'name' | 'meaning';
const MAX = 12;
const newId = () => `val-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 5)}`;

export function Valores() {
  useViewMeta({ nav: 'strategy', crumb: ['Strategy', 'Valores'], j: .40 });
  const nav = useNavigate();
  const { tool, local, setLocal, commit, flush, saveState, status } = useToolDraft('valores');
  const suggest = useValoresSuggest();
  const consolidate = useConsolidate();
  const [editing, setEditing] = useState<{ id: string; field: Field } | null>(null);
  const [sugs, setSugs] = useState<Record<string, string>>({});
  const [note, setNote] = useState<string | null>(null);

  /* o histórico, o autor e a data são do servidor: vêm da última resposta, não do rascunho */
  const meta = useMemo(() => new Map((tool.data?.content.values ?? []).map(v => [v.id!, v])), [tool.data]);
  const values = local?.values ?? [];

  const patch = (id: string, p: Partial<Valor>) => { if (local) commit({ values: local.values.map(v => (v.id === id ? { ...v, ...p } : v)) }); };
  const edit = (id: string, field: Field) => {
    if (editing?.id === id && editing.field === field) { setEditing(null); flush(); return; }
    flush();
    /* uma sessão de edição nova: o que for digitado até concluir é uma edição só no histórico */
    if (local) setLocal({ values: local.values.map(v => (v.id === id ? { ...v, sess: newValorSession() } : v)) });
    setEditing({ id, field });
  };
  const add = () => {
    if (!local || local.values.length >= MAX) return;
    const id = newId();
    commit({ values: [...local.values, { id, name: '', meaning: '', at: '', by: '', ia: false, sess: newValorSession(), history: [] }] });
    setEditing({ id, field: 'name' });
  };
  const remove = (id: string) => { if (local) commit({ values: local.values.filter(v => v.id !== id) }); if (editing?.id === id) setEditing(null); };
  const restore = (id: string, x: ValorVersion) => patch(id, { name: x.name, meaning: x.meaning, ia: x.ia, sess: newValorSession() });

  /* a sugestão: preenche os vazios; nos que já têm texto, fica ao lado para aceitar */
  const ask = (ids: string[] = []) => suggest.mutate(ids, {
    onSuccess: r => {
      setNote(r.note);
      if (!local) return;
      const byId = new Map(r.items.map(x => [x.id, x.meaning]));
      const next: ValoresContent = { values: local.values.map(v => (byId.has(v.id!) && !v.meaning.trim() ? { ...v, meaning: byId.get(v.id!)!, ia: true, sess: newValorSession() } : v)) };
      commit(next);
      setSugs(s => {
        const out = { ...s };
        for (const v of local.values) { const m = byId.get(v.id!); if (m && v.meaning.trim() && m !== v.meaning) out[v.id!] = m; }
        return out;
      });
    },
  });
  const accept = (id: string) => { patch(id, { meaning: sugs[id]!, ia: true, sess: newValorSession() }); drop(id); };
  const drop = (id: string) => setSugs(s => { const { [id]: _, ...rest } = s; return rest; });
  const named = values.filter(v => v.name.trim()).length;

  return (
    <section className="view on" data-v="valores">
      <button className="back" type="button" onClick={() => { flush(); nav('/strategy'); }}><Ic.ChevronLeft />Voltar para Strategy</button>
      <div className="vhead">
        <div>
          <div className="eyebrow"><span className="rule" /><span>Strategy</span><b>Ferramenta 05 / 10</b></div>
          <h1>Valores</h1>
          <p className="lede">Defina os princípios que orientam decisões, comportamentos e relações.</p>
        </div>
        <div className="tools">
          {status && <span className={`g-save ${saveState}`} aria-live="polite">{saveState === 'saved' && <Ic.Check />}{status}</span>}
        </div>
      </div>

      {!local ? <div className="loading-line" /> : (
        <div className="va-grid">
          <section className="card va-card">
            <header className="va-h">
              <div>
                <h3>Lista de valores
                  <span className="g-help" tabIndex={0} role="img" aria-label="Ajuda" data-tip="Os valores são poucos — de quatro a oito — e cada um cabe numa palavra ou duas. A caneta edita e guarda o histórico das últimas cinco versões."><Ic.Help /></span>
                </h3>
                <p>Defina os valores que guiam as decisões e o comportamento da organização.</p>
              </div>
            </header>
            <ol className="va-list">
              {values.map((v, n) => (
                <Row key={v.id} n={n + 1} v={v} meta={meta.get(v.id!)} field="name" editing={editing?.id === v.id && editing?.field === 'name'}
                  onEdit={() => edit(v.id!, 'name')} onChange={x => patch(v.id!, { name: x })} onRemove={() => remove(v.id!)}
                  onRestore={x => restore(v.id!, x)} />
              ))}
            </ol>
            {values.length < MAX
              ? <button type="button" className="addlink va-add" onClick={add}><Ic.Plus />Adicionar valor</button>
              : <p className="subhead">Até {MAX} valores — os que realmente orientam as decisões.</p>}
          </section>

          <section className="card va-card">
            <header className="va-h">
              <div>
                <h3>Detalhamento dos valores
                  <span className="g-help" tabIndex={0} role="img" aria-label="Ajuda" data-tip="Como cada valor aparece nas decisões e no dia a dia. A IA pode escrever os detalhamentos a partir do propósito, da visão e da tríade — você edita."><Ic.Help /></span>
                </h3>
                <p>Descreva como cada valor se manifesta nas decisões e no dia a dia da organização.</p>
              </div>
              <button type="button" className="btn-ghost sm" onClick={() => ask()} disabled={suggest.isPending || !named}>
                {suggest.isPending ? <span className="spin" /> : <Ic.Sparkle />}Sugestão via IA</button>
            </header>
            {!values.length && <p className="gd-empty">Os valores da lista aparecem aqui para o detalhamento.</p>}
            <ol className="va-list">
              {values.map((v, n) => (
                <Row key={v.id} n={n + 1} v={v} meta={meta.get(v.id!)} field="meaning" editing={editing?.id === v.id && editing?.field === 'meaning'}
                  onEdit={() => edit(v.id!, 'meaning')} onChange={x => patch(v.id!, { meaning: x, ia: false })} onRemove={() => remove(v.id!)}
                  onRestore={x => restore(v.id!, x)}
                  suggestion={sugs[v.id!]} onAccept={() => accept(v.id!)} onDismiss={() => drop(v.id!)} />
              ))}
            </ol>
            {note && <p className="gd-note"><Ic.Warning />{note}</p>}
          </section>
        </div>
      )}

      <Actions>
        <button className="btn-ghost" type="button" onClick={() => { flush(); nav('/strategy'); }}>Salvar e sair</button>
        <button className="btn-ghost" type="button" onClick={() => { flush(); nav('/strategy/visao'); }}>Voltar</button>
        <button className="btn-primary" type="button" disabled={consolidate.isPending}
          onClick={() => { flush(); consolidate.mutate(undefined, { onSettled: () => nav('/strategy/bmc') }); }}>
          {consolidate.isPending ? 'Consolidando o DNA…' : 'Continuar'}<Ic.ArrowRight />
        </button>
      </Actions>
    </section>
  );
}

/* ── uma linha: o nome (quadro 1) ou o detalhamento (quadro 2) ────── */

function Row({ n, v, meta, field, editing, onEdit, onChange, onRemove, onRestore, suggestion, onAccept, onDismiss }: {
  n: number; v: Valor; meta?: Valor; field: Field; editing: boolean;
  onEdit: () => void; onChange: (x: string) => void; onRemove: () => void; onRestore: (x: ValorVersion) => void;
  suggestion?: string; onAccept?: () => void; onDismiss?: () => void;
}) {
  const [confirm, setConfirm] = useState(false);
  const box = useRef<HTMLTextAreaElement & HTMLInputElement>(null);
  useEffect(() => { if (!confirm) return; const t = setTimeout(() => setConfirm(false), 3500); return () => clearTimeout(t); }, [confirm]);
  useEffect(() => { if (editing) { const b = box.current; b?.focus(); b?.setSelectionRange(b.value.length, b.value.length); } }, [editing]);
  const name = v.name || `valor ${n}`;
  const history = (meta?.history ?? []).filter(x => (field === 'name' ? x.name : x.meaning).trim());
  const text = field === 'name' ? v.name : v.meaning;

  return (
    <li className={`va-row${editing ? ' editing' : ''}${field === 'meaning' ? ' detail' : ''}`}>
      <span className="va-n mono">{n}</span>
      <div className="va-box">
        {field === 'meaning' && <b className="va-name">{v.name || <em>Sem nome</em>}</b>}
        {editing ? (
          field === 'name'
            ? <input ref={box} className="va-in" value={v.name} maxLength={60} placeholder="Nome do valor" aria-label={`Nome do valor ${n}`}
              onChange={e => onChange(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onEdit(); } }} />
            : <textarea ref={box} className="va-ta" value={v.meaning} maxLength={240} rows={2} placeholder="Como este valor aparece nas decisões e no dia a dia…"
              aria-label={`Detalhamento de ${name}`} onChange={e => onChange(e.target.value)} onKeyDown={e => { if (e.key === 'Escape') onEdit(); }} />
        ) : (
          <span className={`va-text${text.trim() ? '' : ' empty'}`} onDoubleClick={onEdit}>
            {v.ia && field === 'meaning' && text.trim() && <Ic.Sparkle className="va-ai" aria-label="Sugerido pela IA" />}
            {text.trim() || (field === 'name' ? 'Sem nome' : 'Sem detalhamento')}
          </span>
        )}
      </div>
      <div className="va-act">
        <button type="button" className={`va-btn${editing ? ' on' : ''}`} onClick={onEdit} aria-pressed={editing}
          aria-label={editing ? `Concluir edição de ${name}` : `Editar ${field === 'name' ? 'o nome' : 'o detalhamento'} de ${name}`}
          title={editing ? 'Concluir' : `Editar${history.length ? ` · ${history.length} ${history.length === 1 ? 'versão anterior' : 'versões anteriores'}` : ''}`}>
          {editing ? <Ic.Check /> : <Pencil />}
          {!editing && history.length > 0 && <i className="va-dot" />}
        </button>
        {confirm
          ? <button type="button" className="g-del va-del" onClick={onRemove}>Excluir</button>
          : <button type="button" className="va-btn" onClick={() => setConfirm(true)} aria-label={`Excluir ${name}`}><Trash /></button>}
      </div>

      {editing && (
        <div className="va-hist">
          <p className="va-hist-h"><Ic.Clock />Histórico{history.length === 1 ? ' · a última edição' : history.length ? ` · as últimas ${history.length} edições` : ''}
            {meta?.by && meta.at && <span>Versão atual: {meta.by}, {dateTime(meta.at)}</span>}</p>
          {history.length ? (
            <ol>
              {history.map((x, i) => (
                <li key={i}>
                  <div>
                    <span className="va-when">{dateTime(x.at)}{x.by ? ` · ${x.by}` : ''}{x.ia ? ' · sugestão da IA' : ''}</span>
                    <p>{field === 'name' ? x.name : <><b>{x.name !== v.name ? `${x.name}: ` : ''}</b>{x.meaning}</>}</p>
                  </div>
                  <button type="button" className="d-link" onClick={() => onRestore(x)}><Ic.Refresh />Restaurar</button>
                </li>
              ))}
            </ol>
          ) : <p className="va-hist-e">Sem edições anteriores. As próximas cinco ficam guardadas aqui.</p>}
        </div>
      )}

      {suggestion && !editing && (
        <div className="va-sug">
          <span className="tri-target"><Ic.Sparkle />Sugestão da IA</span>
          <p>{suggestion}</p>
          <div className="tri-actions">
            <button type="button" className="btn-primary sm" onClick={onAccept}>Usar<Ic.Check /></button>
            <button type="button" className="btn-ghost sm" onClick={onDismiss}>Manter o atual</button>
          </div>
        </div>
      )}
    </li>
  );
}

const Pencil = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 20h4L19 9a2.8 2.8 0 0 0-4-4L4 16z" /><path d="m13.5 6.5 4 4" /></svg>;
const Trash = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 7h16M9 7V4.5h6V7M6.5 7l1 12.5h9l1-12.5M10 11v5.5M14 11v5.5" /></svg>;
